import { z } from "zod";
// Keep shared validation compatible with the browser's no-eval Content Security Policy.
z.config({ jitless: true });
export const printFormatSchema = z.enum([
    "trade-bw-6x9",
    "trade-color-6x9",
    "trade-color-8.5x8.5",
    "picture-color-8.5x8.5",
    "comic-color",
]);
export const templateSchema = z.object({
    id: z.string().regex(/^[a-z][a-z0-9-]+$/),
    version: z.number().int().positive(),
    workflowKind: z.enum(["INTERVIEW", "PICTURE_BOOK"]).default("INTERVIEW"),
    name: z.string().min(2),
    shortName: z.string().min(2),
    priceUsd: z.number().int().min(79).max(99),
    art: z.enum(["photo", "ai"]),
    printFormat: printFormatSchema,
    tagline: z.string().min(8),
    giftLine: z.string().min(8),
    voiceLabel: z.string().min(8),
    dedication: z.string().min(8),
    palette: z.object({
        cover: z.string().regex(/^#[0-9A-F]{6}$/i),
        coverText: z.string().regex(/^#[0-9A-F]{6}$/i),
        accent: z.string().regex(/^#[0-9A-F]{6}$/i),
        chip: z.string().regex(/^#[0-9A-F]{6}$/i),
    }),
    prompts: z.array(z.string().min(12)).min(12),
});
export const bookTemplates = [
    {
        id: "story",
        version: 1,
        workflowKind: "INTERVIEW",
        name: "My Story",
        shortName: "My Story",
        priceUsd: 99,
        art: "photo",
        printFormat: "trade-bw-6x9",
        tagline: "A life, told from the beginning.",
        giftLine: "The book every family wishes they’d made in time.",
        voiceLabel: "Written as a warm memoir, in their words",
        dedication: "Told in your own words, kept forever.",
        palette: { cover: "#0F4444", coverText: "#F6F1E4", accent: "#C9A45C", chip: "#E4694F" },
        prompts: [
            "What's your very first memory?",
            "Describe the house you grew up in — walk me through the front door.",
            "What were your parents like when you were small?",
            "What did a normal day look like when you were ten?",
            "Who was your childhood best friend, and what did you two get up to?",
            "What was school like — and what kind of student were you, honestly?",
            "Tell me about your teenage years. What did you get away with?",
            "What was your very first job, and what did it pay?",
            "When did you first leave home, and how did it feel?",
            "How did you meet the love of your life?",
            "Tell me about a day you'll never forget.",
            "What was the hardest decision you ever made?",
            "What's a mistake that turned out to be a gift?",
            "What did work teach you that school never could?",
            "Tell me about becoming a parent — or the family you built.",
            "What's a tradition you hope never dies?",
            "What are you most proud of?",
            "What do you want the grandkids to know about you?",
            // Asked first, printed as the book's "Before you begin": never a chapter of its own.
            "Why are you telling these stories, and who are they for?",
        ],
    },
    {
        id: "mother",
        version: 1,
        workflowKind: "INTERVIEW",
        name: "A Mother's Story",
        shortName: "Mother’s Story",
        priceUsd: 89,
        art: "photo",
        printFormat: "trade-bw-6x9",
        tagline: "Her whole life — not just the part you saw.",
        giftLine: "Because she was somebody before she was Mom.",
        voiceLabel: "Written as a tender memoir, in her words",
        dedication: "For everything you gave, and everything you were before us.",
        palette: { cover: "#7A3B52", coverText: "#F7EFE8", accent: "#E7C2B4", chip: "#C9A45C" },
        prompts: [
            "What's your earliest memory of your own mother?",
            "What kind of girl were you growing up?",
            "What did you dream about becoming?",
            "Tell me about your first love — or your first heartbreak.",
            "What was your life like right before kids?",
            "Tell me about the day you became a mother.",
            "What surprised you most about being a mom?",
            "What was the hardest season of raising us, honestly?",
            "What's a moment with your kids you replay in your head?",
            "What did you give up that nobody ever noticed?",
            "What's something your mother did that you repeated — or swore you never would?",
            "What made you laugh harder than anything?",
            "When were you proudest of yourself — not of us, of you?",
            "What do you know about love that took years to learn?",
            "What do you still want to do?",
            "What do you hope we say when we tell your story?",
        ],
    },
    {
        id: "dad",
        version: 1,
        workflowKind: "INTERVIEW",
        name: "A Father's Story",
        shortName: "Father’s Story",
        priceUsd: 89,
        art: "photo",
        printFormat: "trade-bw-6x9",
        tagline: "Dad's stories, from the beginning.",
        giftLine: "The gift he’ll never admit made him cry.",
        voiceLabel: "Written as a wry, honest memoir, in his words",
        dedication: "For Dad — because we finally asked.",
        palette: { cover: "#1E2A3A", coverText: "#EFE9DC", accent: "#C08A3E", chip: "#8A9BB4" },
        prompts: [
            "What kind of kid were you? Be honest.",
            "Tell me about your father — what did he teach you, on purpose or by accident?",
            "What did you want to be when you were twelve?",
            "Tell me about your first car.",
            "What was your first real job — and your worst one?",
            "What did you dream about at 20, and how did it turn out?",
            "Tell me about the day you met the love of your life.",
            "What was the moment you found out you'd be a dad?",
            "What scared you most about being a father?",
            "Tell me about a time you were scared and did it anyway.",
            "What's the best advice you ever ignored?",
            "What's your proudest dad moment?",
            "What's the hardest thing you never talked about?",
            "What's something you hope your kids steal from you?",
            "If you could relive one ordinary day, which one?",
            "What do you want to be remembered for?",
        ],
    },
    {
        id: "love",
        version: 1,
        workflowKind: "INTERVIEW",
        name: "Our Story",
        shortName: "Our Story",
        priceUsd: 89,
        art: "photo",
        printFormat: "trade-bw-6x9",
        tagline: "The two of you, from the very start.",
        giftLine: "Anniversaries end. This doesn’t.",
        voiceLabel: "Written as a shared memoir — your “we”",
        dedication: "For us — the whole story, both sides.",
        palette: { cover: "#5C2230", coverText: "#F6ECE6", accent: "#D89AA6", chip: "#C9A45C" },
        prompts: [
            "Where were you each in life right before you met?",
            "How did you two meet — the real version?",
            "What was your actual first impression of each other?",
            "Tell me about your first date, start to finish.",
            "What was the moment you knew?",
            "What almost broke you up early on?",
            "What did the day you committed to each other actually feel like?",
            "What was your first home together like?",
            "What's a tiny everyday ritual that's just yours?",
            "What's the funniest fight you've ever had?",
            "What's the biggest thing you've survived together?",
            "When were you proudest of each other?",
            "How has the way you love each other changed?",
            "What would you tell newlyweds that nobody told you?",
            "What's a day together you'd live again exactly as it was?",
            "What do you still want to do together?",
            // Asked first, printed as the book's "Before you begin": never a chapter of its own.
            "Why are you telling the story of the two of you, and who is it for?",
        ],
    },
    {
        id: "children",
        version: 3,
        workflowKind: "PICTURE_BOOK",
        name: "A Bedtime Story",
        shortName: "Bedtime Story",
        priceUsd: 79,
        art: "ai",
        printFormat: "picture-color-8.5x8.5",
        // One promise for Bedtime everywhere (owner decision 4, 2026-10-15): a story told to a child,
        // not the storyteller's childhood.
        tagline: "Their stories become the bedtime story.",
        giftLine: "A read-aloud picture book, told by someone who loves them.",
        voiceLabel: "Written as a read-aloud children's storybook",
        dedication: "A true story, about someone who loves you very much.",
        palette: { cover: "#D9822B", coverText: "#2B2118", accent: "#0D3040", chip: "#E4694F" },
        prompts: [
            "What's the very first thing you remember from when you were little?",
            "What did your bedroom look like when you were small?",
            "Did you ever have a pet? Tell me everything.",
            "What games did you play before screens existed?",
            "Who was your best friend and what did you two get up to?",
            "What was school like when you were little?",
            "Tell me about the naughtiest thing you ever did.",
            "What food did you hate as a kid? What did you love?",
            "Tell me about a time you were very, very brave.",
            "Tell me about a big adventure — or a time you got lost.",
            "What was the best birthday you ever had?",
            "What's the most magical thing you ever saw growing up?",
            "What did you want to be when you grew up?",
            "What's the most important thing a kid should know?",
        ],
    },
    {
        id: "family",
        version: 1,
        workflowKind: "INTERVIEW",
        name: "The Family Story",
        shortName: "Family Story",
        priceUsd: 99,
        art: "photo",
        printFormat: "trade-color-6x9",
        tagline: "How this family became this family.",
        giftLine: "The record your grandkids will fight over — nicely.",
        voiceLabel: "Written as a family chronicle",
        dedication: "So the ones who come next know how we got here.",
        palette: { cover: "#2E4632", coverText: "#F2EBD8", accent: "#C9A45C", chip: "#B23A2E" },
        prompts: [
            "Where does this family's story begin, as far as you know?",
            "Tell me about your grandparents — the oldest family you actually knew.",
            "How did our family end up in the town where you grew up?",
            "What did your parents do for work, and what did it teach you?",
            "Tell me about the family home everyone still talks about.",
            "What was Sunday dinner like when you were growing up?",
            "Who was the real character in our family?",
            "What's the story that gets told at every family gathering?",
            "Tell me about a wedding, a birth, or a reunion that changed the family.",
            "What's a hard time this family got through together?",
            "What's a family falling-out we should know the truth about?",
            "What recipe or dish carries the most family history?",
            "What heirloom or object matters most, and why?",
            "What tradition did we almost lose?",
            "How did the family change when the kids grew up and scattered?",
            "What does this family do better than any other family?",
            "What do you want the next generation to protect?",
            "If the family had a motto, what would it be — and why?",
        ],
    },
    {
        id: "ancestry",
        version: 1,
        workflowKind: "INTERVIEW",
        name: "Our Origin Story",
        shortName: "Origin Story",
        priceUsd: 99,
        art: "photo",
        printFormat: "trade-bw-6x9",
        tagline: "Where we come from, before it's gone.",
        giftLine: "The old country, the crossing, the name — saved.",
        voiceLabel: "Written as a heritage chronicle",
        dedication: "For the ones who made the journey, and the ones still on it.",
        palette: { cover: "#4A3626", coverText: "#EFE3CD", accent: "#B98A44", chip: "#7A3B52" },
        prompts: [
            "Where does our family originally come from, as far back as you know?",
            "What's the story behind our family name?",
            "Who's the earliest ancestor you know anything about? Tell their story.",
            "What do you know about how our people got here?",
            "What was the journey like — what did they leave behind, and what did they carry?",
            "Where did they first land, and how did they survive those early years?",
            "What languages, foods, or customs came down from the old country?",
            "What did your grandparents tell you about THEIR grandparents?",
            "What work did our people do, generation to generation?",
            "Is there a family legend or mystery no one ever solved?",
            "What was lost along the way that you wish had survived?",
            "What part of our heritage are you proudest of?",
            "What old-country tradition should we bring back?",
            "What would you say to the ancestors who made the journey?",
        ],
    },
    {
        id: "action",
        version: 1,
        workflowKind: "INTERVIEW",
        name: "A Hero's Story",
        shortName: "Hero’s Story",
        priceUsd: 89,
        art: "photo",
        printFormat: "trade-bw-6x9",
        tagline: "Your life. The Hollywood cut.",
        giftLine: "Every fact true. The telling gets the movie treatment.",
        voiceLabel: "Written as an action-adventure novel — starring them",
        dedication: "Based on a true story. All of it.",
        palette: { cover: "#17181D", coverText: "#EDEDEA", accent: "#D23B2E", chip: "#C9A45C" },
        prompts: [
            "Set the scene: where does our hero's story begin?",
            "What's the first dangerous thing you ever did?",
            "Tell me about a rival — work, sports, life. Who was it?",
            "What's the wildest place your life ever took you?",
            "Tell me about a moment when everything went wrong at once.",
            "What's the closest call you ever had?",
            "What's the biggest risk you ever took, and what was at stake?",
            "Tell me about a race against time you actually lived.",
            "When did you stand up to someone bigger than you?",
            "What's the toughest thing you ever had to survive?",
            "Tell me about your greatest comeback.",
            "Who was on your team when it mattered most?",
            "What's the victory nobody saw but you?",
            "What mission are you still on?",
        ],
    },
    {
        id: "lovestory",
        version: 1,
        workflowKind: "INTERVIEW",
        name: "A Love Story",
        shortName: "Love Story",
        priceUsd: 89,
        art: "photo",
        printFormat: "trade-bw-6x9",
        tagline: "Your romance, written like the novel it was.",
        giftLine: "Because it always was a love story.",
        voiceLabel: "Written as a sweeping love story",
        dedication: "A true love story, exactly as it happened.",
        palette: { cover: "#46203F", coverText: "#F5E9F0", accent: "#D4A0B8", chip: "#C9A45C" },
        prompts: [
            "Set the scene: where were you in life right before you met?",
            "The first time you saw them — what do you actually remember?",
            "What did you think of each other — and what were you each hiding?",
            "Tell me about the first date, the real details.",
            "What almost kept you two apart?",
            "Tell me about the moment you fell — the real one, not the official one.",
            "What was the first “I love you” like?",
            "What was the grand gesture — or the quiet one that mattered more?",
            "Tell me about meeting each other's families.",
            "Tell me about the proposal, or the moment you chose each other.",
            "What was the wedding day — or the day it became forever?",
            "What's the hardest chapter of your love story?",
            "What's one scene from your life together you'd put in the movie?",
            "How has the way you love each other changed?",
            "What's the secret that kept you together?",
            "If you could write the last line of your love story, what would it say?",
        ],
    },
    {
        id: "holiday",
        version: 1,
        workflowKind: "INTERVIEW",
        name: "A Holiday Story",
        shortName: "Holiday Story",
        priceUsd: 79,
        art: "photo",
        printFormat: "trade-bw-6x9",
        tagline: "Every December, remembered.",
        giftLine: "Capture the traditions before they’re memories.",
        voiceLabel: "Written as a warm memoir, in their words",
        dedication: "The holidays, the way our family lived them.",
        palette: { cover: "#173B2A", coverText: "#F4EDDD", accent: "#B23A2E", chip: "#C9A45C" },
        prompts: [
            "What did the holidays smell like in your childhood home?",
            "Who always hosted, and what was their house like?",
            "What was on the table at your family's holiday dinner?",
            "What holiday tradition did your parents pass down to you?",
            "What holiday music takes you back, and to where?",
            "Tell me about the best gift you ever received.",
            "Tell me about the best gift you ever gave.",
            "Describe a holiday that didn't go as planned.",
            "What was the first holiday you hosted yourself?",
            "What's a new tradition you started yourself?",
            "Tell me about a holiday someone was missing — and how you got through it.",
            "What's the funniest holiday disaster in family memory?",
            "What's the holiday moment you wish you could freeze?",
            "What do you want the holidays to feel like for the next generation?",
        ],
    },
    {
        id: "recipes",
        version: 2,
        workflowKind: "INTERVIEW",
        name: "The Family Cookbook",
        shortName: "Family Cookbook",
        priceUsd: 99,
        art: "photo",
        printFormat: "trade-color-8.5x8.5",
        tagline: "Recipes with the stories baked in.",
        giftLine: "The tamale recipe, the meatball secret — finally written down.",
        voiceLabel: "Written as recipe cards with the story behind each dish",
        dedication: "Made with love. Now with instructions.",
        palette: { cover: "#8A4B2D", coverText: "#F8F0DF", accent: "#DDB56E", chip: "#2E4632" },
        prompts: [
            "What's the dish everyone begs you to make? Tell me how — and why it matters.",
            "What's the first thing you ever learned to cook, and who taught you?",
            "What was Sunday dinner at your house — walk me through the table.",
            "What recipe came down from your mother or grandmother? How do you make it?",
            "What did the kitchen smell like on holidays — and what was cooking?",
            "What's the recipe you almost took to the grave? Spill it.",
            "What dish fixed a bad day in your house?",
            "What's your secret ingredient — in anything?",
            "Tell me about a kitchen disaster you can laugh about now.",
            "What do you cook when someone needs comforting?",
            "What's the dish you make from memory — no recipe, never measured?",
            "What recipe do you want the grandkids making in fifty years?",
        ],
    },
    {
        id: "comic",
        version: 1,
        workflowKind: "INTERVIEW",
        name: "The Comic Book",
        shortName: "Comic Book",
        priceUsd: 99,
        art: "ai",
        printFormat: "comic-color",
        tagline: "Your life. Issue #1.",
        giftLine: "Grandpa gets an origin story. The grandkids get a superhero.",
        voiceLabel: "Written as comic book panels — captions, art & speech bubbles",
        dedication: "A true story. With sound effects.",
        palette: { cover: "#1B2A8A", coverText: "#FFF8E7", accent: "#FFD23F", chip: "#F2643B" },
        prompts: [
            "Every hero has an origin story. Where does yours begin?",
            "What was your superpower as a kid — the thing you could do that nobody else could?",
            "Who was your nemesis? School, work, the neighborhood — every hero has one.",
            "Who was your sidekick, and what was your greatest caper together?",
            "Describe your secret headquarters — the place that was YOURS.",
            "Your first mission: the first real job, duty, or responsibility. How'd it go?",
            "Tell me about your most epic fail — the issue where the hero loses.",
            "The comeback issue: how did you get back up?",
            "Every comic has a love interest. Tell me about yours.",
            "Who was on your team when the stakes were highest?",
            "The final boss: the biggest thing you ever had to face down.",
            "Issue #100: what's the mission now?",
        ],
    },
];
for (const template of bookTemplates)
    templateSchema.parse(template);
/**
 * The editions offered to customers (owner decision, 2026-10-14): the comic book, the bedtime
 * story, My Story, Our Story and the family cookbook. The rest of the catalog stays defined but is
 * never offered: no voucher starts it and no page lists it.
 */
export const OFFERED_TEMPLATE_IDS = [
    "story",
    "love",
    "children",
    "recipes",
    "comic",
];
export function isOfferedTemplate(templateId) {
    return OFFERED_TEMPLATE_IDS.includes(templateId);
}
export const offeredBookTemplates = bookTemplates.filter(({ id }) => isOfferedTemplate(id));
export function getTemplate(id) {
    return bookTemplates.find((template) => template.id === id);
}
/**
 * The public pages the web build prerenders, without a leading slash. Each is served as its own
 * HTML, so a search result or link preview reads the page's own title and description; the web
 * distribution's router and the build's prerender step both read this list.
 */
export const publicPrerenderedPagePaths = [
    "books",
    "demo",
    "demo/memoir",
    "demo/bedtime",
    "demo/family",
    "how-it-works",
    "bedtime-story-demo",
    "pricing",
    "faq",
    "accessibility",
    "privacy",
    "terms",
    "refunds",
    "ai-and-media-consent",
    ...bookTemplates.map(({ id }) => `books/${id}`),
];
//# sourceMappingURL=catalog.js.map